import { useEffect, useMemo } from 'react';

import {
    isServer,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
    type UseQueryResult,
} from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { getQueryClient } from 'app/get-query-client';
import { createApiClient } from 'lib/api/createApiClient';
import type { EventVo } from 'models/event';
import type { MarketVo } from 'models/market';
import type { GetEventsResponse, GetMarketResponse, GetSeriesResponse } from 'models/reqres';
import type { SeriesVo } from 'models/series';
import { fetchMarketByTicker, marketKeys, useMarketByTickerQuery } from 'queries/market';
import { marketEntityKeys } from 'queries/marketEntityKeys';
import type { EventEntity, MarketEntity, SeriesEntity } from 'queries/marketEntityTransform';
import {
    eventMarketVoToMarketEntity,
    eventVoToEventEntity,
    marketVoToMarketEntity,
    seriesVoToSeriesEntity,
} from 'queries/marketEntityTransform';
import { subscribeToMarketMetadataChange, useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import type { EventLoader } from 'stores/zustand/orderPanelBridge';
import config from 'utils/config';
import { reloadFunctiontWithDelayAndRetries } from 'utils/functions';
import { buildQueryString } from 'utils/query';

/**
 * First REST hydration path for `marketEntitiesStore` — the piece that was
 * still missing after Phase 0 (transforms + shadow store) and Phase 2 (WS
 * bridge): without it, the store had nothing for the WS bridge to patch or
 * for a future consumer selector to read. Part of the phased removal of
 * `MarketStore` — see `~/.claude/plans/create-a-plan-to-mutable-conway.md`.
 *
 * Reuses the same `/events/?tickers=` bulk endpoint `queries/portfolioEvents.ts`
 * already fetches through in production, rather than the callback-style
 * `getEvent`/`getMarket` in `transport/domains/markets.ts`.
 *
 * This does NOT yet replace any of `MarketStore.resolveEvent`/`loadEvent`'s
 * ~100+ call sites — that bulk migration is the rest of Phase 4. This is
 * additive infrastructure only, not wired into any real page/component yet.
 */

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const MARKET_ENTITY_STALE_TIME_MS = 120 * 1000;

/**
 * `staleTime` for the nested canonical single-ticker `fetchQuery` calls in the batched-cache
 * fetchers. A polling caller (finite `refetchInterval`, e.g. `KnockoutTimeNav`'s 5s settlement
 * poll) needs each poll tick to bypass the 120s singleton freshness window and issue a real REST
 * request — otherwise the nested `fetchQuery` keeps returning the still-fresh cached singleton and
 * the poll never refreshes. Polling only runs client-side, so this returns `0` only in the browser;
 * on the server (and for non-polling callers) it keeps the 120s window.
 */
function canonicalFetchStaleTime(refetchInterval: number | false | undefined): number {
    if (!isServer && typeof refetchInterval === 'number') return 0;
    return MARKET_ENTITY_STALE_TIME_MS;
}

type TickerEntity = { ticker: string };

/** Coalesces canonical single-ticker misses that start together into one bulk request. */
function createTickerBatchLoader<T extends TickerEntity>(fetchBatch: (tickers: string[]) => Promise<T[]>) {
    let pending = new Map<string, Array<{ resolve: (entities: T[]) => void; reject: (error: unknown) => void }>>();
    let scheduled = false;

    const flush = async (): Promise<void> => {
        scheduled = false;
        const batch = pending;
        pending = new Map();

        try {
            const entities = await fetchBatch(Array.from(batch.keys()));
            const entityByTicker = new Map(entities.map((entity) => [entity.ticker, entity]));
            for (const [ticker, waiters] of batch) {
                const entity = entityByTicker.get(ticker);
                for (const waiter of waiters) waiter.resolve(entity ? [entity] : []);
            }
        } catch (error) {
            for (const waiters of batch.values()) {
                for (const waiter of waiters) waiter.reject(error);
            }
        }
    };

    return (ticker: string): Promise<T[]> => {
        const result = new Promise<T[]>((resolve, reject) => {
            const waiters = pending.get(ticker) ?? [];
            waiters.push({ resolve, reject });
            pending.set(ticker, waiters);
        });
        if (!scheduled) {
            scheduled = true;
            queueMicrotask(() => void flush());
        }
        return result;
    };
}

const eventBatchLoaders = new WeakMap<QueryClient, ReturnType<typeof createTickerBatchLoader<EventVo>>>();
const seriesBatchLoaders = new WeakMap<QueryClient, ReturnType<typeof createTickerBatchLoader<SeriesVo>>>();

function getEventBatchLoader(queryClient: QueryClient) {
    let loader = eventBatchLoaders.get(queryClient);
    if (!loader) {
        loader = createTickerBatchLoader((tickers) => fetchEventsByTickers(tickers));
        eventBatchLoaders.set(queryClient, loader);
    }
    return loader;
}

function getSeriesBatchLoader(queryClient: QueryClient) {
    let loader = seriesBatchLoaders.get(queryClient);
    if (!loader) {
        loader = createTickerBatchLoader((tickers) => fetchSeriesByTickers(tickers));
        seriesBatchLoaders.set(queryClient, loader);
    }
    return loader;
}

// Re-exported for existing consumers — defined in a standalone, hook-free module
// (see `marketEntityKeys.ts`) so server-only callers can import it without this
// file's React-hook exports.
export { marketEntityKeys };

/** Mirrors the deleted `MarketStore.loadEvents`' chunking: the endpoint has no cursor/page_size
 * handling for a `tickers` filter, so a request for more tickers than the backend's default page
 * silently drops the overflow instead of erroring. */
const EVENTS_PAGE_CHUNK_SIZE = 100;

async function fetchEventsByTickersChunk(tickers: string[], signal?: AbortSignal): Promise<EventVo[]> {
    // `with_markdown: true` mirrors the removed `MarketStore.loadEvent/getEvent`'s request — without it,
    // an event reloaded through this path (e.g. `handleMarketMetadataChange`) comes back with non-markdown/
    // empty rule text and `product_metadata.important_info`, overwriting the markdown-rich SSR snapshot.
    const response = await api.get<GetEventsResponse>('/events/', {
        params: { tickers: tickers.join(','), with_markdown: true },
        signal,
    });
    return response.data.events ?? [];
}

export async function fetchEventsByTickers(tickers: string[], signal?: AbortSignal): Promise<EventVo[]> {
    if (tickers.length === 0) return [];
    if (tickers.length <= EVENTS_PAGE_CHUNK_SIZE) return fetchEventsByTickersChunk(tickers, signal);

    const chunks: string[][] = [];
    for (let i = 0; i < tickers.length; i += EVENTS_PAGE_CHUNK_SIZE) {
        chunks.push(tickers.slice(i, i + EVENTS_PAGE_CHUNK_SIZE));
    }
    const results = await Promise.all(chunks.map((chunk) => fetchEventsByTickersChunk(chunk, signal)));
    return results.flat();
}

async function fetchEventsThroughCanonicalCache(
    queryClient: QueryClient,
    tickers: string[],
    staleTime: number | 'static',
    signal?: AbortSignal,
): Promise<EventVo[]> {
    const uniqueTickers = Array.from(new Set(tickers));
    // This query already owns the canonical single-ticker key. Nesting fetchQuery with the same
    // key would wait on itself, so keep the singleton path direct.
    if (uniqueTickers.length <= 1) return fetchEventsByTickers(uniqueTickers, signal);

    const load = getEventBatchLoader(queryClient);
    const perTicker = await Promise.all(
        uniqueTickers.map((ticker) =>
            queryClient.fetchQuery({
                queryKey: marketEntityKeys.eventsByTickers([ticker]),
                queryFn: () => load(ticker),
                staleTime,
                // The enclosing `useQuery` already owns the retry loop. Leaving the default
                // two-retry policy on these inner canonical queries makes both layers retry: the
                // per-ticker retries fire independently after the shared bulk request rejects, miss
                // the microtask-only batch window, and fan a single failed batch out into one
                // `/events/` request per ticker on every retry. Only one layer should retry.
                retry: false,
            }),
        ),
    );
    return perTicker.flat();
}

export interface UseEventsByTickersOptions {
    enabled?: boolean;
    refetchInterval?: number | false;
}

export function useEventsByTickers(
    tickers: string[],
    options: UseEventsByTickersOptions = {},
): UseQueryResult<EventVo[]> {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: marketEntityKeys.eventsByTickers(tickers),
        queryFn: ({ signal }) =>
            fetchEventsThroughCanonicalCache(
                queryClient,
                tickers,
                canonicalFetchStaleTime(options.refetchInterval),
                signal,
            ),
        enabled: tickers.length > 0 && (options.enabled ?? true),
        refetchInterval: options.refetchInterval,
        // Matches the QueryClient's default (see `app/get-query-client.ts`) and is repeated here
        // because nested canonical `fetchQuery` calls need the same 120s freshness rule.
        // `handleMarketMetadataChange` below is wired to `marketEntitiesStore`'s
        // metadata-change listeners, but nothing notifies them yet: the `market_metadata`
        // WS channel isn't deployed on the exchange gateway (subscribing gets a hard
        // rejection), and since the store no longer owns a WS subscription its future
        // reducer belongs in `tickerQuotesStore`. Until then this path is inert. Without a
        // finite staleTime, a long-lived tab that visited an event before a
        // title/rules/market-list edit would keep hydrating the stale VO forever.
        staleTime: MARKET_ENTITY_STALE_TIME_MS,
        // SSR and every bulk fetch populate canonical single-ticker entries, while several
        // callers still use a batched key (e.g. `PrimaryContent` siblings and prop events).
        // Synthesize initial data when every requested singleton is present so those callers
        // render immediately instead of waiting for their own batched query to settle.
        initialData: () => {
            if (tickers.length <= 1) return undefined;
            const perTicker = tickers.map((t) =>
                queryClient.getQueryData<EventVo[]>(marketEntityKeys.eventsByTickers([t])),
            );
            if (perTicker.some((entry) => entry === undefined)) return undefined;
            return perTicker.flatMap((entry) => entry ?? []);
        },
    });
}

/**
 * Fetches `tickers`' events via REST and hydrates each event + its markets into
 * `marketEntitiesStore`. Safe to call from multiple mounted components — the
 * underlying query is deduped/cached per-ticker-set by React Query, and
 * hydration is idempotent (an upsert keyed by ticker).
 */
export function useHydrateEventsIntoStore(
    tickers: string[],
    options: UseEventsByTickersOptions = {},
): UseQueryResult<EventVo[]> {
    const query = useEventsByTickers(tickers, options);
    const actions = useMarketEntitiesStore((state) => state.actions);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!query.data) return;
        // Single bulk write. Looping `eventHydrated`/`marketHydrated` per market here did one
        // `set` each, and since store consumers read via `useSyncExternalStore` (synchronous
        // re-render per `set`), a many-market event turned one hydration pass into dozens of
        // nested synchronous re-renders — tripping React's "Maximum update depth exceeded" on the
        // home slideshow. `eventsHydrated` collapses the whole batch into one store update and
        // protects every event in the batch from evicting the others.
        actions.eventsHydrated(
            query.data.map((eventVo) => ({
                event: eventVoToEventEntity(eventVo),
                markets: eventVo.markets.map((marketVo) => eventMarketVoToMarketEntity(marketVo, eventVo.ticker)),
            })),
        );
        seedMarketEntitiesIntoCache(queryClient, query.data);
    }, [query.data, actions, queryClient]);

    return query;
}

export interface UsePollEventIntoStoreOptions {
    /** `true` → `config.pollingInterval` (4s default); `false` → `config.slowPollingInterval` (60s default). */
    fast?: boolean;
    enabled?: boolean;
}

/**
 * Like `useHydrateEventsIntoStore`, but re-fetches on an interval — for callers that
 * need `Event.startPolling`/`stopPolling`'s "keep this one event fresh while it's on
 * screen" behavior. Mirrors `useExchangeStatus`'s shape (`queries/exchangeStatus.ts`).
 * Not yet wired into any page — the WS-vs-poll policy decision (skip polling entirely
 * for logged-in members once `market_lifecycle_v2` is connected, same as the original
 * version) is left to the caller via `enabled`, since that policy doesn't belong in a
 * generic hydration hook.
 */
export function usePollEventIntoStore(
    ticker: string,
    options: UsePollEventIntoStoreOptions = {},
): UseQueryResult<EventVo[]> {
    return useHydrateEventsIntoStore([ticker], {
        enabled: !!ticker && (options.enabled ?? true),
        refetchInterval: options.fast ? config.pollingInterval : config.slowPollingInterval,
    });
}

export interface UsePollMarketIntoStoreOptions {
    enabled?: boolean;
    refetchInterval?: number;
}

/** Keeps one selected market fresh without re-downloading its entire event on every poll. */
export function usePollMarketIntoStore(
    ticker: string,
    options: UsePollMarketIntoStoreOptions = {},
): ReturnType<typeof useMarketByTickerQuery> {
    const query = useMarketByTickerQuery(ticker, {
        enabled: !!ticker && (options.enabled ?? true),
        refetchInterval: options.refetchInterval ?? config.pollingInterval,
        refetchIntervalInBackground: false,
        staleTime: 0,
    });
    const actions = useMarketEntitiesStore((state) => state.actions);

    useEffect(() => {
        if (!query.data) return;
        const market = marketVoToMarketEntity(query.data);
        const existingEventTicker = useMarketEntitiesStore.getState().markets[market.ticker]?.eventTicker;
        actions.marketHydrated(existingEventTicker ? { ...market, eventTicker: existingEventTicker } : market);
    }, [query.data, actions]);

    return query;
}

// Re-exported for existing consumers — defined in `marketEntitySeed.ts`, a standalone,
// hook-free module, so `prefetchEventPageQueries.ts` (a Server Component's helper) can seed
// the SSR payload into the request-scoped `QueryClient` without pulling this file's React-hook
// exports into the Server Component graph. Their cache keys are what `useHydrateEventsIntoStore`/
// `useHydrateSeriesIntoStore` below read — every real call site hydrates one event/series ticker
// at a time, e.g. `useEventPage.ts`, `useSwitchEvent.ts`'s commit path.
export { seedEventEntitiesIntoCache, seedSeriesEntitiesIntoCache } from 'queries/marketEntitySeed';

/**
 * Seeds the per-id `marketById` cache entry (`marketEntityKeys.byId(id)`) for each market in
 * `events` — the same key `useMarketAndEventByMarketId`/`useHydrateMarketsByIds` read, so a caller
 * that later needs one of these markets by id gets an instant cache hit within the default
 * `staleTime` window instead of its own `/markets/{id}` round trip. Fill-only: skips any id that
 * already has cached data, so it never clobbers a fresher, independently-resolved direct fetch for
 * the same id (this is a one-time prime, not an ongoing sync — once a caller's own `useQuery`
 * mounts for that key, its normal fetch/staleTime lifecycle takes over). Used by
 * `useHydrateEventsIntoStore` below, and by any SSR-seed path (e.g. `HubPage`'s own `initialEvents`
 * seed) that already has `EventVo`s in hand.
 *
 * Takes the containing `EventVo`s (not a flattened `MarketVo[]`) because `ranged_group_ticker` —
 * which `useMarketAndEventByMarketId`/`useCachedMarketAndEventByMarketId` read to resolve the
 * parent event — is optional on `MarketVo` and absent from markets embedded in an `/events`
 * payload; each seeded row is filled in with its own event's ticker when missing, so by-id
 * consumers can still derive the parent event/series from the cached row.
 *
 * ASSUMPTION: markets embedded in an `/events` payload are otherwise field-complete `MarketVo`s,
 * identical to what `GET /markets/{id}` returns (aside from the `ranged_group_ticker` fill above).
 * That's what makes short-circuiting the dedicated `/markets/{id}` fetch (via a fresh `byId` cache
 * hit) safe. This holds today — the event page itself renders full market tiles (prices, volume,
 * strikes) straight off these embedded markets through the same `marketVoToMarketEntity` transform
 * these by-id consumers use (`useEventPage.ts`) — so any missing field would already break the
 * event page. If the backend ever ships a thinner embedded `MarketVo`, by-id consumers would read
 * a degraded entity for the seeded row's `staleTime` window; seed only the fields that are
 * guaranteed present, or drop the `byId` seed, if that ever changes.
 */
export function seedMarketEntitiesIntoCache(queryClient: QueryClient, events: EventVo[]): void {
    for (const event of events) {
        for (const market of event.markets) {
            const key = marketEntityKeys.byId(market.id);
            if (queryClient.getQueryData(key) !== undefined) continue;
            queryClient.setQueryData(key, {
                ...market,
                ranged_group_ticker: market.ranged_group_ticker ?? event.ticker,
            });
        }
    }
}

export async function fetchSeriesByTickers(tickers: string[], signal?: AbortSignal): Promise<SeriesVo[]> {
    if (tickers.length === 0) return [];
    const response = await api.get<GetSeriesResponse>(`/series/${buildQueryString({ series_tickers: tickers })}`, {
        signal,
    });
    return response.data.series ?? [];
}

async function fetchSeriesThroughCanonicalCache(
    queryClient: QueryClient,
    tickers: string[],
    staleTime: number | 'static',
    signal?: AbortSignal,
): Promise<SeriesVo[]> {
    const uniqueTickers = Array.from(new Set(tickers));
    if (uniqueTickers.length <= 1) return fetchSeriesByTickers(uniqueTickers, signal);

    const load = getSeriesBatchLoader(queryClient);
    const perTicker = await Promise.all(
        uniqueTickers.map((ticker) =>
            queryClient.fetchQuery({
                queryKey: marketEntityKeys.seriesByTickers([ticker]),
                queryFn: () => load(ticker),
                staleTime,
                // See `fetchEventsThroughCanonicalCache`: the outer `useQuery` owns the retry loop,
                // so inner canonical retries would just fan a failed batch out per ticker.
                retry: false,
            }),
        ),
    );
    return perTicker.flat();
}

export function useSeriesByTickers(
    tickers: string[],
    options: UseEventsByTickersOptions = {},
): UseQueryResult<SeriesVo[]> {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: marketEntityKeys.seriesByTickers(tickers),
        queryFn: ({ signal }) =>
            fetchSeriesThroughCanonicalCache(
                queryClient,
                tickers,
                canonicalFetchStaleTime(options.refetchInterval),
                signal,
            ),
        enabled: tickers.length > 0 && (options.enabled ?? true),
        refetchInterval: options.refetchInterval,
        // Cache-components-safe (`'static'` on the server); see `useEventsByTickers` above.
        staleTime: MARKET_ENTITY_STALE_TIME_MS,
        // Mirrors `useEventsByTickers`' canonical-cache synthesis above for callers that pass
        // several series tickers at once (e.g. batched Hub/Elections/PrimaryContent hydration).
        initialData: () => {
            if (tickers.length <= 1) return undefined;
            const perTicker = tickers.map((t) =>
                queryClient.getQueryData<SeriesVo[]>(marketEntityKeys.seriesByTickers([t])),
            );
            if (perTicker.some((entry) => entry === undefined)) return undefined;
            return perTicker.flatMap((entry) => entry ?? []);
        },
    });
}

/** Fetches `tickers`' series via REST and hydrates each into `marketEntitiesStore`. */
export function useHydrateSeriesIntoStore(
    tickers: string[],
    options: UseEventsByTickersOptions = {},
): UseQueryResult<SeriesVo[]> {
    const query = useSeriesByTickers(tickers, options);
    const actions = useMarketEntitiesStore((state) => state.actions);

    useEffect(() => {
        if (!query.data) return;
        for (const seriesVo of query.data) {
            actions.seriesHydrated(seriesVoToSeriesEntity(seriesVo));
        }
    }, [query.data, actions]);

    return query;
}

/**
 * Batches `eventTickers` into one `useHydrateEventsIntoStore` call, then chains into their
 * parent series with a second batched `useHydrateSeriesIntoStore` call — for callers (a
 * bracket/hub/portfolio page or table) that need every visible row's event AND series hydrated
 * up front so each row can read both passively, instead of independently hydrating its own
 * ticker. Extracted from four call sites (`BracketsHalfWidth`, `HubPage`,
 * `CasualPositionPortfolio`, `PositionPortfolioTable`) that had each copy-pasted this exact
 * event-then-series chain.
 */
export function useHydrateEventsAndSeriesIntoStore(
    eventTickers: string[],
    options: UseEventsByTickersOptions = {},
): void {
    useHydrateEventsIntoStore(eventTickers, options);
    const eventsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(eventTickers.map((t) => [t, s.events[t]]))),
    );
    const seriesTickers = useMemo(
        () =>
            Array.from(
                new Set(eventTickers.map((t) => eventsByTicker[t]?.seriesTicker).filter((t): t is string => !!t)),
            ),
        [eventTickers, eventsByTicker],
    );
    useHydrateSeriesIntoStore(seriesTickers);
}

export interface EventAndSeriesEntities {
    event: EventEntity | undefined;
    series: SeriesEntity | undefined;
}

/**
 * Read-only counterpart to `useHydrateEventsIntoStore`/`useHydrateSeriesIntoStore` — resolves
 * an event ticker's already-hydrated event + series from `marketEntitiesStore` without ever
 * fetching. For descendants of a page root that has ALREADY hydrated this exact ticker (e.g.
 * `useEventPage`'s `[eventTicker]` hydrate at the Event page root) — mirrors
 * `useCachedMarketAndEventByMarketId`'s reasoning. Do NOT use this for a ticker that isn't
 * guaranteed hydrated by an ancestor (e.g. a value read from a separate zustand store, session
 * storage, or a route param with no hydrating parent) — those callers should keep an active
 * `useHydrateEventsIntoStore` call.
 */
export function useEventAndSeriesEntities(eventTicker: string | undefined): EventAndSeriesEntities {
    const event = useMarketEntitiesStore((s) => (eventTicker ? s.events[eventTicker] : undefined));
    const series = useMarketEntitiesStore((s) => (event ? s.series[event.seriesTicker] : undefined));
    return { event, series };
}

async function fetchMarketById(marketId: string, signal?: AbortSignal): Promise<MarketVo | undefined> {
    const response = await api.get<GetMarketResponse>(`/markets/${marketId}`, { signal });
    return response.data.market;
}

/**
 * Imperative (non-hook) resolve of a market + its parent event to plain entities, by market **id**.
 * For callers that need the data inside an effect/callback rather than at render — e.g. a browser
 * push-notification body built after the notification arrives. Fetches through the RQ cache with the
 * SAME keys the hook counterparts use (`useMarketAndEventByMarketId`/`useHydrateEventsIntoStore`), so
 * it dedups against any concurrent render-time fetch. Returns `undefined` if the market can't load.
 */
export async function fetchMarketAndEventEntityById(
    marketId: string,
): Promise<{ market: MarketEntity; event: EventEntity | undefined } | undefined> {
    const marketVo = await getQueryClient().fetchQuery({
        queryKey: marketEntityKeys.byId(marketId),
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchMarketById(marketId, signal),
    });
    if (!marketVo) return undefined;

    const eventTicker = marketVo.ranged_group_ticker;
    let eventVo: EventVo | undefined;
    if (eventTicker) {
        const events = await getQueryClient().fetchQuery({
            queryKey: marketEntityKeys.eventsByTickers([eventTicker]),
            queryFn: ({ signal }: { signal?: AbortSignal }) => fetchEventsByTickers([eventTicker], signal),
        });
        eventVo = events.find((e) => e.ticker === eventTicker);
    }

    return {
        market: marketVoToMarketEntity(marketVo),
        event: eventVo ? eventVoToEventEntity(eventVo) : undefined,
    };
}

export interface MarketAndEventByMarketId {
    market: MarketEntity | undefined;
    event: EventEntity | undefined;
    series: SeriesEntity | undefined;
}

/**
 * Resolves a market by **id** (not ticker) via REST, then chains into its event/series —
 * for the rare callers (e.g. account history rows) that only have a `market_id`, unlike
 * everything else in this file which is keyed by ticker. Hydrates all three into
 * `marketEntitiesStore` as a side effect, same as the ticker-keyed hooks above.
 */
export function useMarketAndEventByMarketId(marketId: string | undefined): MarketAndEventByMarketId {
    const actions = useMarketEntitiesStore((state) => state.actions);
    const marketQuery = useQuery({
        queryKey: marketEntityKeys.byId(marketId ?? ''),
        queryFn: ({ signal }) => fetchMarketById(marketId ?? '', signal),
        enabled: !!marketId,
    });

    useEffect(() => {
        if (marketQuery.data) {
            actions.marketHydrated(marketVoToMarketEntity(marketQuery.data));
        }
    }, [marketQuery.data, actions]);

    const eventTicker = marketQuery.data?.ranged_group_ticker;
    useHydrateEventsIntoStore(eventTicker ? [eventTicker] : []);
    const eventEntity = useMarketEntitiesStore((s) => (eventTicker ? s.events[eventTicker] : undefined));
    useHydrateSeriesIntoStore(eventEntity ? [eventEntity.seriesTicker] : []);
    const seriesEntity = useMarketEntitiesStore((s) => (eventEntity ? s.series[eventEntity.seriesTicker] : undefined));

    const marketTicker = marketQuery.data?.ticker_name;
    const market = useMarketEntitiesStore((s) => (marketTicker ? s.markets[marketTicker] : undefined));

    return { market, event: eventEntity, series: seriesEntity };
}

/**
 * Read-only counterpart to `useMarketAndEventByMarketId` — resolves the same cache entries
 * without ever fetching. For high-fan-out row lists (e.g. a paginated Account activity feed,
 * up to 20 rows/page) where the market/event/series only enrich a row's link text — a
 * nice-to-have, not worth a `/markets/{id}` + event/series round trip per row. Surfaces data
 * only when some other caller (e.g. `useMarketAndEventByMarketId`, `useHydrateMarketsByIds`)
 * already hydrated it; callers should fall back to their own display text (e.g. the
 * operation's own `market_title`) when this returns `undefined` fields.
 */
export function useCachedMarketAndEventByMarketId(marketId: string | undefined): MarketAndEventByMarketId {
    const queryClient = useQueryClient();
    const marketVo = marketId
        ? queryClient.getQueryData<MarketVo | undefined>(marketEntityKeys.byId(marketId))
        : undefined;

    const eventTicker = marketVo?.ranged_group_ticker;
    const eventEntity = useMarketEntitiesStore((s) => (eventTicker ? s.events[eventTicker] : undefined));
    const seriesEntity = useMarketEntitiesStore((s) => (eventEntity ? s.series[eventEntity.seriesTicker] : undefined));

    const marketTicker = marketVo?.ticker_name;
    const market = useMarketEntitiesStore((s) => (marketTicker ? s.markets[marketTicker] : undefined));

    return { market, event: eventEntity, series: seriesEntity };
}

/**
 * Batch counterpart to `useMarketAndEventByMarketId` for prefetch-only callers that hold a
 * variable-length list of market ids and just need them hydrated into `marketEntitiesStore`
 * (e.g. `useNotifications` warming the store for market-only notification cards). Uses the
 * SAME per-id query key as `useMarketAndEventByMarketId`, so a card that later renders and calls
 * that hook gets an instant RQ cache hit rather than refetching. Does not itself chain into each
 * market's parent event/series — it returns the resolved `MarketVo[]` (carrying
 * `ranged_group_ticker`) so a caller that also needs those, like `useNotifications`, can chain a
 * further `useHydrateEventsIntoStore` call and fold its `isLoading` into its own loading gate,
 * rather than reporting done before a card's own `useMarketAndEventByMarketId` event fetch has.
 */
export function useHydrateMarketsByIds(marketIds: string[]): { isLoading: boolean; marketVos: MarketVo[] } {
    const actions = useMarketEntitiesStore((state) => state.actions);
    const results = useQueries({
        queries: marketIds.map((id) => ({
            queryKey: marketEntityKeys.byId(id),
            queryFn: ({ signal }: { signal: AbortSignal }) => fetchMarketById(id, signal),
            enabled: !!id,
        })),
    });

    const loadedMarkets = results.map((r) => r.data).filter((d): d is MarketVo => !!d);
    const loadedKey = loadedMarkets.map((m) => m.ticker_name).join(',');
    useEffect(() => {
        for (const vo of loadedMarkets) {
            actions.marketHydrated(marketVoToMarketEntity(vo));
        }
        // loadedKey captures the set of hydrated markets; re-run only when it changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedKey, actions]);

    return { isLoading: results.some((r) => r.isLoading), marketVos: loadedMarkets };
}

export interface EventAndSeriesByMarketTicker {
    event: EventEntity | undefined;
    series: SeriesEntity | undefined;
}

/**
 * Resolves a market **ticker** (not id) to its parent event + series via REST,
 * hydrating both into `marketEntitiesStore` — the ticker-keyed counterpart to
 * `useMarketAndEventByMarketId`, for callers that only have a `market_ticker`
 * (e.g. social notification payloads) and need the event/series it belongs to,
 * not the market entity itself.
 */
export function useEventAndSeriesByMarketTicker(marketTicker: string | undefined): EventAndSeriesByMarketTicker {
    const marketQuery = useMarketByTickerQuery(marketTicker);
    const eventTicker = marketQuery.data?.ranged_group_ticker;
    useHydrateEventsIntoStore(eventTicker ? [eventTicker] : []);
    const event = useMarketEntitiesStore((s) => (eventTicker ? s.events[eventTicker] : undefined));
    useHydrateSeriesIntoStore(event ? [event.seriesTicker] : []);
    const series = useMarketEntitiesStore((s) => (event ? s.series[event.seriesTicker] : undefined));
    return { event, series };
}

export interface MarketWithEventAndSeries {
    market: MarketEntity | undefined;
    event: EventEntity | undefined;
    series: SeriesEntity | undefined;
    /** The parent event's full markets (for event-level derivations like `eventIsTrueBinary`). */
    eventMarkets: MarketEntity[];
}

/**
 * Batched, list-keyed counterpart to `useEventAndSeriesByMarketTicker` for callers that hold a
 * variable-length list of market **tickers** and need each one's market + parent event + series
 * (e.g. the RFQ quote/request lists grouping rows by event). Fetches each market by ticker through
 * the SAME `marketKeys.byTicker` cache key `useMarketByTickerQuery` uses (so single-ticker callers
 * dedup), hydrates markets + their events + series into `marketEntitiesStore`, and returns a map
 * keyed by market ticker. Entries resolve to `undefined` fields until their fetches settle.
 */
export function useMarketsByTickers(marketTickers: string[]): {
    byTicker: Record<string, MarketWithEventAndSeries>;
    isLoading: boolean;
} {
    const actions = useMarketEntitiesStore((state) => state.actions);
    const results = useQueries({
        queries: marketTickers.map((ticker) => ({
            queryKey: marketKeys.byTicker(ticker),
            queryFn: ({ signal }: { signal: AbortSignal }) => fetchMarketByTicker(ticker, signal),
            enabled: !!ticker,
        })),
    });

    const marketVos = results.map((r) => r.data).filter((d): d is MarketVo => !!d);
    const marketHydrateKey = marketVos.map((m) => m.ticker_name).join(',');
    useEffect(() => {
        for (const vo of marketVos) {
            actions.marketHydrated(marketVoToMarketEntity(vo));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketHydrateKey, actions]);

    // ticker -> parent event ticker, from the fetched VOs (MarketEntity only carries it optionally).
    const eventTickerByMarketTicker = useMemo(() => {
        const map: Record<string, string | undefined> = {};
        for (const vo of marketVos) {
            map[vo.ticker_name] = vo.ranged_group_ticker;
        }
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketHydrateKey]);

    const eventTickers = useMemo(
        () => [...new Set(Object.values(eventTickerByMarketTicker).filter((t): t is string => !!t))],
        [eventTickerByMarketTicker],
    );
    const eventsQuery = useHydrateEventsIntoStore(eventTickers);
    const eventsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(eventTickers.map((t) => [t, s.events[t]]))),
    );

    const seriesTickers = useMemo(
        () => [...new Set(eventTickers.map((t) => eventsByTicker[t]?.seriesTicker).filter((t): t is string => !!t))],
        [eventTickers, eventsByTicker],
    );
    const seriesQuery = useHydrateSeriesIntoStore(seriesTickers);
    const seriesByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(seriesTickers.map((t) => [t, s.series[t]]))),
    );

    // All markets belonging to the resolved events (each event hydrates all its markets into
    // the store), for event-level derivations like `eventIsTrueBinary`.
    const allEventMarketTickers = useMemo(
        () => [...new Set(eventTickers.flatMap((t) => eventsByTicker[t]?.marketTickers ?? []))],
        [eventTickers, eventsByTicker],
    );
    const eventMarketByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(allEventMarketTickers.map((t) => [t, s.markets[t]]))),
    );

    const marketsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(marketTickers.map((t) => [t, s.markets[t]]))),
    );

    const byTicker = useMemo(() => {
        const map: Record<string, MarketWithEventAndSeries> = {};
        for (const ticker of marketTickers) {
            const eventTicker = eventTickerByMarketTicker[ticker];
            const event = eventTicker ? eventsByTicker[eventTicker] : undefined;
            const eventMarkets = event
                ? event.marketTickers.map((t) => eventMarketByTicker[t]).filter((m): m is MarketEntity => !!m)
                : [];
            map[ticker] = {
                market: marketsByTicker[ticker],
                event,
                series: event ? seriesByTicker[event.seriesTicker] : undefined,
                eventMarkets,
            };
        }
        return map;
    }, [
        marketTickers,
        eventTickerByMarketTicker,
        eventsByTicker,
        seriesByTicker,
        marketsByTicker,
        eventMarketByTicker,
    ]);

    // Includes the chained event/series hydration queries, not just the market lookups —
    // otherwise a caller gating an empty state on `isLoading` (e.g. AllRestingOrdersTable) can
    // flip to "loaded" while events/series are still in flight, and groupRestingOrdersByEvent
    // (which drops any order whose event isn't resolved yet) shows an empty state despite
    // orders existing.
    return { byTicker, isLoading: results.some((r) => r.isLoading) || eventsQuery.isLoading || seriesQuery.isLoading };
}

export async function loadEventIntoStoreOnce(ticker: string): Promise<boolean> {
    const events = await getQueryClient().fetchQuery({
        queryKey: marketEntityKeys.eventsByTickers([ticker]),
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchEventsByTickers([ticker], signal),
    });
    const eventVo = events.find((e) => e.ticker === ticker);
    if (!eventVo) return false;

    const actions = useMarketEntitiesStore.getState().actions;
    actions.eventHydrated(eventVoToEventEntity(eventVo));
    for (const marketVo of eventVo.markets) {
        actions.marketHydrated(eventMarketVoToMarketEntity(marketVo, eventVo.ticker));
    }
    return true;
}

/**
 * Reload an event's REST data after a `market_metadata` WS message — mirrors the removed
 * `MarketStore.reloadEventWithDelayAndRetries` (rule text/title/custom strike/media changes never
 * reach a mounted event page any other way; `ticker`/`market_lifecycle_v2` only carry price and
 * lifecycle fields). Delayed 2s to accommodate BE lag between the WS message and the exchange's
 * own write landing, then retried up to 4 times 1s apart.
 *
 * `loadEventIntoStoreOnce`'s `fetchQuery` alone wouldn't force a real network read here — a
 * recently-hydrated event is still within the default `staleTime` window, so it would just hand
 * back the (pre-metadata-change) cached VOs. Invalidate first so the fetch is genuine.
 *
 * Invalidates every `eventsByTickers` entry that includes this ticker via a predicate, not just
 * the single-ticker key: `eventsByTickers` also caches batched hydrations (e.g.
 * `eventsByTickers(['A', 'B'])`), and RQ's default partial-key matching only matches a query whose
 * key has the given key as a positional prefix — `['A']` isn't a prefix of `['A', 'B']`. Left
 * unfixed, a still-fresh batched entry could later replay its stale `EventVo` over the freshly
 * reloaded entity.
 */
export async function handleMarketMetadataChange(eventTicker: string): Promise<void> {
    await reloadFunctiontWithDelayAndRetries(
        async () => {
            await getQueryClient().invalidateQueries({
                predicate: (query) => {
                    const [namespace, kind, tickers] = query.queryKey;
                    return (
                        namespace === marketEntityKeys.all[0] &&
                        kind === 'events' &&
                        Array.isArray(tickers) &&
                        tickers.includes(eventTicker)
                    );
                },
            });
            await loadEventIntoStoreOnce(eventTicker);
        },
        4,
        2000,
        1000,
    );
}

if (typeof window !== 'undefined') {
    subscribeToMarketMetadataChange((eventTicker) => {
        void handleMarketMetadataChange(eventTicker);
    });
}

/**
 * Plain (non-hook) `EventLoader` (see `stores/zustand/orderPanelBridge.ts`)
 * backed by `marketEntitiesStore` + React Query — a drop-in replacement for
 * the former `MarketStore` wherever the duck-typed `EventLoader` interface is
 * consumed outside a component's render path (e.g. `commitOrderPanelTicker`
 * call sites), so those call sites no longer need `useStores().marketStore`.
 *
 * `notFound`/`onFinally` mirror `MarketStore.loadEvent`'s callback contract;
 * REST/network errors are swallowed here rather than routed to a snackbar —
 * that side effect isn't part of the `EventLoader` contract itself.
 */
export const marketEntitiesEventLoader: EventLoader = {
    resolveEvent: (ticker) => ({ loaded: !!useMarketEntitiesStore.getState().events[ticker] }),
    loadEvent: (ticker, notFound = () => {}, onFinally = () => {}) => {
        if (useMarketEntitiesStore.getState().events[ticker]) {
            onFinally();
            return;
        }
        loadEventIntoStoreOnce(ticker)
            .then((found) => {
                if (!found) notFound();
            })
            .catch(() => {
                // Swallow — see doc comment above.
            })
            .finally(onFinally);
    },
};
